import React from 'react';
import { VEREDICTO_META } from './veredictoHelpers';
import ProductSalesMetrics from '../../../components/productos/Buscador/ProductSalesMetrics';
import { resolveSalesRisk } from './ocSalesAlerts';
import OcStockConteoBlock from './OcStockConteoBlock';

/**
 * Insights en lenguaje llano para el líder en la vista pública de aprobación.
 * Lee indicadores congelados de la línea (sin API extra).
 * Conteo en vivo no aplica aquí (magic link sin permiso de grabar).
 */
const OcAprobarInsight = ({ line }) => {
  const ind = line?.indicadores || {};
  const veredicto = line?.veredicto || ind.veredicto;
  const meta = VEREDICTO_META[veredicto] || VEREDICTO_META.posponer;
  const label = line?.label || ind.label || meta.label;

  const stock = ind.stock_actual != null ? Number(ind.stock_actual) : null;
  const dias = ind.dias_inventario != null ? Number(ind.dias_inventario) : null;
  const prioridad = ind.prioridad || null;
  const yhat = ind.yhat != null ? Number(ind.yhat) : null;
  const rotacion = ind.estado_rotacion && ind.estado_rotacion !== 'normal'
    ? ind.estado_rotacion
    : null;
  const sugerencia = ind.sugerencia_inventario || null;
  const alertaProv = ind.alerta_proveedor?.mensaje || ind.alerta_precio?.mensaje || null;
  const duplicados = Array.isArray(ind.duplicados_oc) ? ind.duplicados_oc : [];
  const motivo = line.motivo_override || line.demanda_latente_nota || null;
  const { sinVentas, sinVentas30 } = resolveSalesRisk(ind);

  let demandaFrase = null;
  if (prioridad) {
    demandaFrase = yhat != null
      ? `Demanda ${prioridad} (yhat ${yhat})`
      : `Demanda ${prioridad}`;
  }

  return (
    <div className={`mt-3 rounded-xl border-2 p-3 space-y-2 ${meta.className}`}>
      <div>
        <p className="text-[11px] font-extrabold uppercase tracking-wide opacity-70">
          Qué pasa con este producto
        </p>
        <p className="text-lg font-black leading-snug mt-0.5">{label}</p>
      </div>

      {!line.es_producto_nuevo && (
        <OcStockConteoBlock
          idProducto={line.id_producto || ind.id_producto}
          nombreProducto={line.nombre_producto}
          stockActual={stock}
          stockMinimo={ind.stock_minimo}
          diasInventario={dias}
          tieneMovimientosRecientes={ind.tieneMovimientosRecientes}
          nombreEmpleado={ind.nombre_empleado}
          fechaUltimoMovimiento={ind.fecha_ultimo_movimiento}
          canGrabar={false}
          tooltipId={`aprobar-conteo-${line.id_detalle || line.id_producto}`}
        />
      )}

      {(demandaFrase || rotacion) && (
        <ul className="space-y-1.5 text-base font-semibold">
          {demandaFrase && <li>{demandaFrase}</li>}
          {rotacion && <li>Rotación: {rotacion}</li>}
        </ul>
      )}

      {!line.es_producto_nuevo && (
        <ProductSalesMetrics
          compact
          producto={{
            ventas_30_dias: ind.ventas_30_dias,
            cantidad_ventas: ind.cantidad_ventas,
            fecha_ultima_venta: ind.fecha_ultima_venta,
          }}
        />
      )}

      {sinVentas && !line.es_producto_nuevo && (
        <p className="text-base font-bold text-red-950 bg-red-100 border-2 border-red-400 rounded-xl px-3 py-2.5 leading-snug">
          Este producto no tiene ventas. Pedirlo es riesgoso; confirma que hay demanda real.
        </p>
      )}

      {!sinVentas && sinVentas30 && (
        <p className="text-sm font-semibold text-amber-950 bg-amber-100 border border-amber-300 rounded-lg px-2.5 py-2">
          Sin ventas en 30 días.
        </p>
      )}

      {sugerencia && (
        <p className="text-sm font-medium leading-snug opacity-95 bg-white/50 rounded-lg px-2.5 py-2">
          {sugerencia}
        </p>
      )}

      {alertaProv && (
        <p className="text-sm font-semibold text-amber-950 bg-amber-100/90 border border-amber-300 rounded-lg px-2.5 py-2">
          {alertaProv}
        </p>
      )}

      {duplicados.length > 0 && (
        <div className="text-sm font-semibold text-amber-950 bg-amber-100/90 border border-amber-300 rounded-lg px-2.5 py-2">
          <p className="font-bold">Ya está en otra orden abierta</p>
          <ul className="mt-1 space-y-0.5 font-medium">
            {duplicados.slice(0, 3).map((d) => (
              <li key={d.id_orden}>
                {d.numero_orden}
                {d.nombre_proveedor ? ` · ${d.nombre_proveedor}` : ''}
                {d.cantidad != null ? ` · qty ${d.cantidad}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {motivo && (
        <p className="text-sm font-semibold bg-white/60 rounded-lg px-2.5 py-2">
          <span className="opacity-70">Motivo del analista: </span>
          {motivo}
        </p>
      )}
    </div>
  );
};

export default OcAprobarInsight;
